/**
 * Fully procedural WebAudio engine. No sample files — every sound in the game
 * is synthesised at runtime from oscillators, noise buffers and filters.
 *
 * Layers, quietest first:
 *   music     a record playing somewhere else, every few minutes  (music.ts)
 *   ambience  the room itself, including the fluorescent hum      (beds.ts, hum.ts)
 *   tension   an adaptive bed driven by the director              (tension.ts)
 *   whispers  half-heard voices, gated on sanity                  (whispers.ts)
 *   sfx       every one-shot                                      (sfx.ts)
 *
 * The `AudioContext` is created lazily inside `resume`, which browsers require
 * to be reached from a user gesture. Until then — and forever, if the platform
 * has no WebAudio at all — every method is a safe no-op and the game runs
 * silently rather than not at all. State set before the context exists
 * (volume, mute, ambience, tension) is remembered and applied on creation.
 */

import { TUNING } from '../core/config';
import { clamp01 } from '../core/math';
import type {
  AmbienceProfile,
  AudioAPI,
  EntityKind,
  EventBus,
  ExitPoint,
  GameContext,
  ItemKind,
  SfxName,
  Vec2,
} from '../core/types';
import { Ambience } from './ambience';
import { AudioGraph } from './graph';
import { HoundSong } from './houndsong';
import { Music } from './music';
import { Sfx, type PlayOptions } from './sfx';
import { setListener as aimListener } from './spatial';
import { Tension } from './tension';
import { makeVoiceChat, type VoiceChat, type VoiceChatSystem } from './voicechat';
import { Whispers } from './whispers';

/**
 * Sounds that both the event bus and a subsystem might reasonably trigger.
 * See `bridge` below for why the engine cares.
 */
type Category =
  | 'step'
  | 'pickup'
  | 'consume'
  | 'hurt'
  | 'death'
  | 'flashlight'
  | 'transition'
  | 'ambient-event'
  | 'entity-vocal';

const CATEGORY: Partial<Record<SfxName, Category>> = {
  'step-carpet': 'step',
  'step-concrete': 'step',
  'step-water': 'step',
  'step-tile': 'step',
  pickup: 'pickup',
  'consume-drink': 'consume',
  'consume-food': 'consume',
  hurt: 'hurt',
  death: 'death',
  'flashlight-click': 'flashlight',
  'flashlight-dying': 'flashlight',
  descend: 'transition',
  noclip: 'transition',
  'distant-thud': 'ambient-event',
  'distant-scrape': 'ambient-event',
  'distant-voice': 'ambient-event',
  'slide-settle': 'ambient-event',
  'hound-scream': 'entity-vocal',
  'smiler-laugh': 'entity-vocal',
  'centipede-surge': 'entity-vocal',
  'partygoer-step': 'entity-vocal',
};

/**
 * What each kind makes when it lands a hit.
 *
 * Fired from `entity:attack`, not from the behaviours — no behaviour plays
 * anything on contact, so an absent row here is an entity that hits you in
 * silence. The five canonical kinds borrow the same sounds `entities/voice.ts`
 * borrows for their voices, so the thing that hits you sounds like the thing
 * that was coming.
 */
const ATTACK_SFX: Partial<Record<EntityKind, SfxName>> = {
  hound: 'hound-scream',
  smiler: 'smiler-laugh',
  bacteria: 'distant-voice',
  faceling: 'smiler-breath',
  deathmoth: 'smiler-breath',
  partygoer: 'smiler-laugh',
  centipede: 'centipede-surge',
  clump: 'hurt',
  hollow: 'whisper',
};

/**
 * What using an exit sounds like. Both of these were fully synthesised and
 * never played by anything, because nothing in the game read `ExitPoint.kind`.
 *
 * This fires on `level:exitFound` and is the way out *opening*; `level:descend`
 * then plays 'descend', which is the fall. So no entry here may be 'descend'
 * itself — a slide mapped to it played the same 3.4 s one-shot twice, on top of
 * itself. 'slide-settle' is the tube taking your weight instead.
 */
const EXIT_SFX: Record<ExitPoint['kind'], SfxName> = {
  door: 'door',
  stairwell: 'door',
  noclip: 'noclip',
  hole: 'noclip',
  slide: 'slide-settle',
};

/** What an off-screen fakeout sounds like. */
const FAKEOUT_SFX: readonly SfxName[] = ['distant-thud', 'distant-scrape', 'distant-voice'];

const STEP_SFX: Record<'carpet' | 'concrete' | 'water' | 'tile', SfxName> = {
  carpet: 'step-carpet',
  concrete: 'step-concrete',
  water: 'step-water',
  tile: 'step-tile',
};

/**
 * Sounds the hound's song has to get out of the way of, whoever played them.
 *
 * Routed here rather than through `CATEGORY` because these must duck the song
 * without also ceding anything: the growl is played by the behaviour itself
 * (`entities/behaviors/hound.ts`), so it arrives as a direct `play` and never
 * through the bridge, and the cede machinery below must not learn about it.
 *
 * The growl is the player's taught warning and the scream is the commit. Not
 * 'hound-step' — the footfalls run every 0.32 s during a chase, and the song is
 * already floored by `MODE_DUCK.chase` by the time they are audible.
 */
const SONG_DUCKING_SFX: ReadonlySet<SfxName> = new Set<SfxName>(['hound-growl', 'hound-scream']);

const DRINKABLE: ReadonlySet<ItemKind> = new Set<ItemKind>(['almond-water', 'water-bottle', 'pills']);
const EDIBLE: ReadonlySet<ItemKind> = new Set<ItemKind>(['granola', 'canned-food', 'candy']);

class Engine implements AudioAPI {
  private graph: AudioGraph | null = null;
  private sfx: Sfx | null = null;
  private ambience: Ambience | null = null;
  private tension: Tension | null = null;
  private whispers: Whispers | null = null;
  private music: Music | null = null;
  private song: HoundSong | null = null;

  /**
   * Other players, both the ones on a microphone and the ones who are only
   * breathing. Built up front and inert until the network layer attaches a
   * socket to it, so a game with no server is a game with a voice chat that
   * never does anything.
   */
  readonly voice: VoiceChatSystem = makeVoiceChat();

  /** Set once WebAudio has proven unavailable, so we stop retrying. */
  private failed = false;
  private gestureArmed = false;

  private volume = TUNING.audio.masterVolume;
  private muted = false;
  private wantAmbience: AmbienceProfile = 'silence';
  private wantIntensity = 1;
  private wantTension = 0;
  private lx = 0;
  private lz = 0;
  private lyaw = 0;

  private bus: EventBus | null = null;
  private readonly unbind: Array<() => void> = [];
  /** Categories the rest of the game turned out to play for itself. */
  private readonly ceded = new Set<Category>();
  private readonly bridgedAt = new Map<Category, number>();
  private readonly collisions = new Map<Category, number>();

  get ready(): boolean {
    return this.graph !== null && this.graph.ctx.state === 'running';
  }

  async resume(): Promise<void> {
    if (this.failed) return;
    if (!this.graph) {
      // Constructed here rather than at boot: browsers refuse to start a
      // context outside a gesture, and a dead context cannot be revived.
      const w = window as unknown as {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      };
      const Ctor = w.AudioContext ?? w.webkitAudioContext;
      if (!Ctor) {
        this.failed = true;
        return;
      }
      try {
        this.build(new Ctor({ latencyHint: 'interactive' }));
      } catch {
        this.failed = true;
        return;
      }
    }

    const ctx = this.graph!.ctx;
    if (ctx.state !== 'running') {
      try {
        await ctx.resume();
      } catch {
        /* still suspended; the gesture hook will try again */
      }
    }
    if (ctx.state === 'running') this.disarmGesture();
    else this.armGesture();
  }

  play(name: SfxName, opts?: PlayOptions): void {
    if (!this.sfx) return;
    this.noteDirectPlay(name);
    this.duckMusic(CATEGORY[name]);
    this.duckSong(name);
    this.sfx.play(name, opts);
  }

  setListener(pos: Vec2, yaw: number): void {
    this.lx = pos.x;
    this.lz = pos.z;
    this.lyaw = yaw;
    if (this.graph) aimListener(this.graph.listener, pos.x, pos.z, yaw);
  }

  setAmbience(profile: AmbienceProfile, intensity = 1): void {
    this.wantAmbience = profile;
    this.wantIntensity = clamp01(intensity);
    this.ambience?.set(profile, this.wantIntensity);
    // The music sits at a fixed distance under the bed, and the beds are not
    // all the same loudness, so it has to be told which room it is under.
    this.music?.setAmbience(profile);
  }

  setTension(value: number): void {
    this.wantTension = clamp01(value);
    this.tension?.setTarget(this.wantTension);
  }

  setMasterVolume(v: number): void {
    this.volume = clamp01(v);
    this.graph?.setMaster(this.volume, this.muted);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.graph?.setMaster(this.volume, this.muted);
  }

  update(dt: number, ctx: GameContext): void {
    const g = this.graph;
    // The voice chat runs even before the context exists: the microphone
    // prompt is DOM, not audio, and withdrawing it on a timer has to keep
    // working on a machine that never granted us a `AudioContext` at all.
    this.voice.update(dt, ctx);
    if (!g) return;
    this.bridge(ctx.events);

    const step = Math.min(dt, TUNING.maxDeltaTime);
    const sanity = clamp01(ctx.stats.sanity / 100);
    const active = !ctx.dead && !ctx.paused;

    this.ambience!.update(step, 1 - sanity);
    this.tension!.update(step, ctx.stats, active);
    this.whispers!.update(step, sanity, active);
    // After the tension layer, so the music sees this frame's dread rather than
    // the last frame's and never overlaps an encounter by a frame.
    this.music!.update(step, ctx, this.tension!.value);
    // Last of the layers, and after the entities have already moved and growled
    // this frame — `entities` runs before `audio` in the frame order — so the
    // song is placed at where the hound actually is now and any growl it just
    // made has already ducked it.
    this.song!.update(step, ctx.entities, active);
    g.update();
  }

  dispose(): void {
    this.disarmGesture();
    for (const off of this.unbind) off();
    this.unbind.length = 0;
    this.bus = null;
    // Before the context closes: the peer chains hang off its nodes, and the
    // microphone has to be handed back whatever else happens.
    this.voice.setGraph(null);
    this.voice.dispose();
    const g = this.graph;
    // The music layer and the hound's song own media elements as well as nodes,
    // so closing the context is not enough to release them.
    this.music?.dispose();
    this.song?.dispose();
    // Null everything before the async close so a frame that lands in between
    // finds a dead engine rather than a half-torn-down graph.
    this.graph = null;
    this.sfx = null;
    this.ambience = null;
    this.tension = null;
    this.whispers = null;
    this.music = null;
    this.song = null;
    this.failed = true;
    if (g) void g.ctx.close().catch(() => undefined);
  }

  /* ---------------------------------------------------------------- *
   * Construction
   * ---------------------------------------------------------------- */

  private build(ctx: AudioContext): void {
    const g = new AudioGraph(ctx);
    this.graph = g;
    this.sfx = new Sfx(g);
    this.ambience = new Ambience(g, this.sfx);
    this.tension = new Tension(g, this.sfx);
    this.whispers = new Whispers(g, this.sfx);
    this.music = new Music(g);
    // Allocates nothing until a hound turns up; see `houndsong.ts`.
    this.song = new HoundSong(g);

    g.setMaster(this.volume, this.muted);
    aimListener(g.listener, this.lx, this.lz, this.lyaw);
    this.tension.setTarget(this.wantTension);
    this.ambience.set(this.wantAmbience, this.wantIntensity);
    this.music.setAmbience(this.wantAmbience);
    // Peers heard before this point were heard by nothing; their chains are
    // rebuilt against the new context rather than pointing at a dead one.
    this.voice.setGraph(g);
  }

  private armGesture(): void {
    if (this.gestureArmed) return;
    this.gestureArmed = true;
    window.addEventListener('pointerdown', this.onGesture, true);
    window.addEventListener('keydown', this.onGesture, true);
  }

  private disarmGesture(): void {
    if (!this.gestureArmed) return;
    this.gestureArmed = false;
    window.removeEventListener('pointerdown', this.onGesture, true);
    window.removeEventListener('keydown', this.onGesture, true);
  }

  /** Some browsers ignore the first resume; take the next gesture instead. */
  private readonly onGesture = (): void => {
    void this.resume();
  };

  /* ---------------------------------------------------------------- *
   * Event bridge
   *
   * `makeAudio()` takes no event bus, so the bus is picked up from the first
   * frame's context. Several of these sounds could equally well be triggered
   * by the system that owns the event — the player controller has `ctx.audio`
   * and may play its own footsteps. Rather than guess, the engine plays them
   * and watches: if a direct `play` of the same category lands within a
   * quarter second of a bridged one twice, the category is ceded and the
   * engine stops handling that event. Silence is a worse failure than two
   * doubled footsteps at the start of a run.
   * ---------------------------------------------------------------- */

  private bridge(bus: EventBus): void {
    if (this.bus === bus) return;
    for (const off of this.unbind) off();
    this.unbind.length = 0;
    this.bus = bus;

    this.unbind.push(
      bus.on('player:step', ({ surface, speed, pos }) => {
        this.bridged('step', STEP_SFX[surface] ?? 'step-carpet', {
          pos,
          gain: 0.55 + clamp01(speed / 5) * 0.5,
          rate: 0.94 + clamp01(speed / 5) * 0.12,
        });
      }),
    );

    this.unbind.push(bus.on('item:pickup', () => this.bridged('pickup', 'pickup')));

    this.unbind.push(
      bus.on('item:consume', ({ kind }) => {
        const name: SfxName = DRINKABLE.has(kind) ? 'consume-drink' : EDIBLE.has(kind) ? 'consume-food' : 'pickup';
        this.bridged('consume', name);
      }),
    );

    this.unbind.push(
      bus.on('player:hurt', ({ amount }) => {
        this.bridged('hurt', 'hurt', { gain: 0.6 + clamp01(amount / 25) * 0.5 });
      }),
    );

    this.unbind.push(bus.on('player:died', () => this.bridged('death', 'death')));

    this.unbind.push(bus.on('flashlight:toggle', () => this.bridged('flashlight', 'flashlight-click')));
    this.unbind.push(bus.on('flashlight:depleted', () => this.bridged('flashlight', 'flashlight-dying')));

    this.unbind.push(
      // Fires immediately before `level:descend`, so this is the way out being
      // opened and the 'descend' one-shot is the fall itself.
      bus.on('level:exitFound', ({ exit }) => {
        this.bridged('transition', EXIT_SFX[exit.kind], { gain: 0.8 });
      }),
    );

    this.unbind.push(
      bus.on('level:descend', () => {
        this.bridged('transition', 'descend');
      }),
    );

    this.unbind.push(
      bus.on('entity:fakeout', ({ pos, flavor }) => {
        // Glimpses and shadows are the renderer's business; only the sound
        // flavour has anything to play.
        if (flavor !== 'sound') return;
        // The director used to pick and play this itself as well; the pool and
        // the gain jitter moved here so there is one emitter and one sound.
        const rng = this.graph!.rng;
        const name = rng.pick(FAKEOUT_SFX);
        this.bridged('ambient-event', name, { pos, gain: rng.range(0.7, 1.0), rate: rng.range(0.88, 1.12) });
      }),
    );

    this.unbind.push(
      bus.on('entity:attack', ({ kind }) => {
        const name = ATTACK_SFX[kind];
        if (name) this.bridged('entity-vocal', name);
      }),
    );
  }

  private bridged(cat: Category, name: SfxName, opts?: PlayOptions): void {
    if (!this.sfx || this.ceded.has(cat)) return;
    this.bridgedAt.set(cat, this.clock());
    this.duckMusic(cat);
    this.duckSong(name);
    this.sfx.play(name, opts);
  }

  /**
   * The music is the only layer that is never the point of the moment it is
   * in, so it gets out of the way of anything that is. Routed through here
   * rather than through the event bus because both the bridge and a direct
   * `play` have to duck, and only one of them will be live for a given sound.
   */
  private duckMusic(cat: Category | undefined): void {
    // Deeper than they were, in step with the layer being louder: the old
    // numbers were set against a mix where the music was 11 dB under the hum
    // and largely got out of the way on its own.
    if (cat === 'entity-vocal') this.music?.dip(0.2, 2.6);
    else if (cat === 'death') this.music?.dip(0.07, 4.5);
  }

  /**
   * Keep the hound's song out from under the hound's own warning. Both `play`
   * and `bridged` route through here, because the wind-up growls arrive as
   * direct plays from the behaviour and the contact scream arrives through the
   * bridge, and every one of them has to punch the same hole.
   */
  private duckSong(name: SfxName): void {
    if (SONG_DUCKING_SFX.has(name)) this.song?.duckUnderVocal();
  }

  private noteDirectPlay(name: SfxName): void {
    const cat = CATEGORY[name];
    if (cat === undefined || this.ceded.has(cat)) return;
    const last = this.bridgedAt.get(cat);
    if (last === undefined || this.clock() - last > 0.25) return;
    const n = (this.collisions.get(cat) ?? 0) + 1;
    this.collisions.set(cat, n);
    if (n >= 2) this.ceded.add(cat);
  }

  /** Wall clock in seconds. Works before the context exists. */
  private clock(): number {
    return performance.now() / 1000;
  }
}

/** The one export `game.ts` knows about. */
export function makeAudio(): AudioAPI {
  return new Engine();
}

/** An `AudioAPI` that also carries the voice chat. See `voiceOf`. */
export interface VoiceCapableAudio extends AudioAPI {
  readonly voice: VoiceChat;
}

/**
 * The voice chat belonging to an audio engine, or null.
 *
 * `AudioAPI` lives in `core/types.ts` and is the contract every subsystem sees;
 * voice chat is not part of it, because a build with no network layer has no
 * business knowing the concept exists. So the network client asks for it by
 * duck-typing rather than by widening the shared interface, and gets null from
 * anything that is not this engine — a test double, a silent stub — without
 * either side having to care.
 *
 * ```ts
 * const voice = voiceOf(this.audio);
 * voice?.attach(transport); // see `VoiceTransport` in `audio/voicechat.ts`
 * ```
 */
export function voiceOf(audio: AudioAPI): VoiceChat | null {
  return (audio as Partial<VoiceCapableAudio>).voice ?? null;
}
